import React, {
  useState,
  useEffect,
  useMemo,
  Fragment,
  useRef
} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import useHundredBricksAPI from '@lib/hundredBricksAPI/useHundredBricksAPI';
import { LocationBlockedModal } from '@views/Login/components';
import { OTPErrorHandler } from '@components/OTPAlert';
import { useAuth } from '@lib/authentication';
import { useProfile } from '@lib/profile';
import { useErrorReporter } from '@lib/errorReporter';
import { USER_AGENT_MESSAGE } from '@constants';
import { Paragraph, Subtitle } from '@components/Styled';
import { useForm, useLocationBlocked, useRequest } from '@hooks';
import {
  TraderIsNotBlocked,
  UnusualAccess,
  UserAgentRequired
} from '@lib/hundredBricksAPI/authentication';
import {
  Button,
  InvestorLocationModal,
  OneTimePassword,
  OTPAlert
} from '@components';

import useGeolocation from '../../hooks/useGeolocation';

import { ButtonGroup, ErrorMessage, UnblockLayout } from './UnusualAccessUnblock.style';

const unblockSchema = {
  code: {
    required: true
  }
};

const UnusualAccessUnblock = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const form = useForm({ schema: unblockSchema });
  const api = useHundredBricksAPI();
  const errorReporter = useErrorReporter();
  const { getCurrentPosition, getGeolocationStatus } = useGeolocation();
  const { profile } = useProfile();

  const {
    error,
    isLoading,
    token,
    unlockUnusualAccess
  } = useAuth();

  const [
    unblockCodeResponse,
    isSendingUnblockCode,
    unblockCodeError,
    sendUnblockCode
  ] = useRequest(api.authentication.sendUnusualCodeAccess);

  const {
    isBlocked: displayLocationBlockedModal,
    reason: blockedReason,
    attempt: locationAttempt,
    block: blockLocation,
    unblock: unblockLocation,
    reset: resetLocationBlock
  } = useLocationBlocked();

  const [verificationCode, setVerificationCode] = useState();
  const [showInvestorLocationModal, setShowInvestorLocationModal] = useState(false);
  const [isApprovingLocation, setIsApprovingLocation] = useState(false);
  const [isVisibleAlert, setIsVisibleAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('');
  const [showUserAgentMessage, setShowUserAgentMessage] = useState(false);

  const alertTimeRef = useRef(null);
  const email = searchParams.get('email')?.toLowerCase();

  const handleSendEmailUnblockCode = () => {
    sendUnblockCode(email, 'email');
    form.removeError('code');
  };

  const handleUnblockInvestor = async () => {
    getCurrentPosition(coords => {
      const { latitude, longitude } = coords;

      unlockUnusualAccess(
        {
          code: verificationCode,
          email,
          latitude,
          longitude
        }
      );
    }, reason => {
      setIsApprovingLocation(false);
      blockLocation(reason);
    });
  };

  const handleVerificationCodeChange = oneTimeCode => {
    setVerificationCode(oneTimeCode);

    form.removeError('code');
  };

  const verificationCodeIsEmpty = useMemo(
    () => verificationCode?.length === 6,
    [verificationCode]
  );

  const acceptShareLocation = () => {
    setShowInvestorLocationModal(false);
    handleUnblockInvestor();
  };

  const cancelShareLocation = () => {
    setShowInvestorLocationModal(false);
    setIsApprovingLocation(false);
  };

  const onSubmit = async () => {
    setShowUserAgentMessage(false);
    setIsApprovingLocation(true);

    const geolocationStatus = await getGeolocationStatus();

    if (geolocationStatus === 'prompt') {
      setShowInvestorLocationModal(true);
    } else if (geolocationStatus === 'denied') {
      blockLocation('denied');
      setIsApprovingLocation(false);
    } else if (geolocationStatus === 'granted') {
      handleUnblockInvestor();
    }
  };

  const showAlert = (message, type) => {
    setAlertMessage(message);
    setAlertType(type);
    setIsVisibleAlert(true);

    if (type === 'SUCCESS') {
      alertTimeRef.current = setTimeout(() => setIsVisibleAlert(false), 5000);
    }
  };

  const handleTryAgain = () => {
    handleSendEmailUnblockCode();
    setIsVisibleAlert(false);
  };

  const handleErrors = OTPErrorHandler({
    form,
    showAlert,
    errorReporter,
    canResendErrorCode: 'CANT_RESEND_TOKEN'
  });

  const unblockErrorMessage = showUserAgentMessage ? USER_AGENT_MESSAGE : undefined;

  const handleSendEmailUnblockCodeRef = useRef(handleSendEmailUnblockCode);
  const showAlertRef = useRef(showAlert);
  const handleErrorsRef = useRef(handleErrors);

  useEffect(() => {
    handleSendEmailUnblockCodeRef.current = handleSendEmailUnblockCode;
    showAlertRef.current = showAlert;
    handleErrorsRef.current = handleErrors;
  });

  useEffect(() => {
    if (!isSendingUnblockCode && !unblockCodeResponse && !unblockCodeError) {
      handleSendEmailUnblockCodeRef.current();
    }
  }, [isSendingUnblockCode, unblockCodeResponse, unblockCodeError]);

  useEffect(() => {
    if (unblockCodeResponse && !unblockCodeError) {
      showAlertRef.current(
        'Se ha enviado el código exitosamente.',
        'SUCCESS'
      );
    } else if (unblockCodeError && !(unblockCodeError instanceof TraderIsNotBlocked)) {
      handleErrorsRef.current(unblockCodeError.cause);
    }
  }, [unblockCodeResponse, unblockCodeError]);

  useEffect(() => {
    if (token && !error && profile) {
      navigate('/marketplace');
    } else if (error && !(error instanceof UnusualAccess)) {
      if (error instanceof UserAgentRequired) {
        setShowUserAgentMessage(true);
      } else {
        handleErrorsRef.current(error.cause);
      }

      setIsApprovingLocation(false);
    }
  }, [token, error, profile, navigate]);

  useEffect(() => () => {
    if (alertTimeRef.current) {
      clearTimeout(alertTimeRef.current);
    }
  }, []);

  return (
    <Fragment>
      <OTPAlert
        isVisible={isVisibleAlert}
        alertType={alertType}
        alertMessage={alertMessage}
        onTryAgain={handleTryAgain}
      />

      <UnblockLayout>
        <Subtitle>Confirma tu identidad</Subtitle>

        {
          !(unblockCodeError instanceof TraderIsNotBlocked) && (
            <Fragment>
              <Paragraph>
                Para iniciar sesión, ingresa tu código de seguridad temporal.
              </Paragraph>

              <OneTimePassword
                error={{ message: form.errors.code }}
                showVerifyButton={false}
                resendOptions={['email']}
                onChange={handleVerificationCodeChange}
                onSendCode={handleSendEmailUnblockCode}
              />

              <ButtonGroup>
                <Button
                  color="secondary"
                  onClick={() => navigate('/login')}
                >
                  Regresar
                </Button>

                <Button
                  color="primary"
                  isLoading={isLoading || isApprovingLocation}
                  disabled={!verificationCodeIsEmpty}
                  onClick={onSubmit}
                >
                  Iniciar sesión
                </Button>
              </ButtonGroup>

              {Boolean(unblockErrorMessage) && (
                <ErrorMessage>
                  {unblockErrorMessage}
                </ErrorMessage>
              )}
            </Fragment>
          )
        }

        {
          (unblockCodeError instanceof TraderIsNotBlocked) && (
            <Fragment>
              <Paragraph>
                Tu cuenta no se encuentra bloqueada. Por favor, inicia sesión para continuar.
              </Paragraph>

              <Button
                type="button"
                onClick={() => { navigate('/login'); }}
              >
                Ir a iniciar sesion
              </Button>
            </Fragment>
          )
        }

        <InvestorLocationModal
          isOpen={showInvestorLocationModal}
          onConfirm={acceptShareLocation}
          onCancel={cancelShareLocation}
        />

        <LocationBlockedModal
          isOpen={displayLocationBlockedModal}
          reason={blockedReason}
          attempt={locationAttempt}
          onConfirm={() => {
            unblockLocation();
            handleUnblockInvestor();
          }}
          onCancel={resetLocationBlock}
        />
      </UnblockLayout>
    </Fragment>
  );
};

export default UnusualAccessUnblock;
